import {inject, Injectable} from '@angular/core'
import {Subject} from 'rxjs'

import {AnalyticsService} from '@app/core/services/google-analytics/google-analytics.service'

export type LogoutSource = 'user' | 'http-401' | 'refresh-failure'

export type LogoutStage =
  | 'started'
  | 'deduplicated'
  | 'local-session-cleared'
  | 'login-suppression-armed'
  | 'keycloak-navigation-started'
  | 'local-login-navigation-started'
  | 'login-suppression-consumed'
  | 'login-suppression-expired'

interface LogoutMarker {
  readonly createdAt: number
  readonly source: LogoutSource
}

export interface LogoutDiagnostic extends LogoutMarker {
  readonly stage: LogoutStage
  readonly destinationOrigin?: string
}

const JUST_LOGGED_OUT_KEY = 'mypayter.just-logged-out'
const LOGOUT_DIAGNOSTIC_KEY = 'mypayter.logout.diagnostic'
const MARKER_MAX_AGE_MS = 10 * 60 * 1000
const MAX_DIAGNOSTIC_ENTRIES = 12

/**
 * Coordinates every logout entry point.
 *
 * Explicit logout, refresh failure and concurrent HTTP 401 responses all pass
 * through this singleton before clearing state or navigating. This prevents a
 * late 401 from cancelling an in-flight cross-origin Keycloak navigation.
 *
 * Diagnostics deliberately contain no token, user, URL query or other account
 * data. A bounded stage trail is kept in sessionStorage so a tester can inspect it
 * after a redirect with:
 * `sessionStorage.getItem('mypayter.logout.diagnostic')`.
 */
@Injectable({providedIn: 'root'})
export class LogoutCoordinatorService {
  private readonly analytics = inject(AnalyticsService)
  private readonly startedSubject = new Subject<LogoutSource>()
  private inProgress = false

  /** First-owner notification used to synchronously close active UI overlays. */
  readonly started$ = this.startedSubject.asObservable()

  begin(source: LogoutSource): boolean {
    if (this.inProgress) {
      this.record('deduplicated', source)
      return false
    }
    this.inProgress = true
    this.startedSubject.next(source)
    this.record('started', source)
    return true
  }

  record(
    stage: LogoutStage,
    source: LogoutSource,
    destinationUrl?: string,
  ): void {
    const diagnostic: LogoutDiagnostic = {
      stage,
      source,
      createdAt: Date.now(),
      ...(destinationUrl
        ? {destinationOrigin: this.safeOrigin(destinationUrl)}
        : {}),
    }

    const history = this.readDiagnosticHistory()
    history.push(diagnostic)
    this.safeSessionStorageSet(
      LOGOUT_DIAGNOSTIC_KEY,
      JSON.stringify(history.slice(-MAX_DIAGNOSTIC_ENTRIES)),
    )

    // This log intentionally stays available in production. Preserve log in
    // DevTools to retain the final pre-navigation stage.
    console.info('[MyPayter logout]', diagnostic)

    try {
      this.analytics.sendEvent('logout_flow', {
        logout_stage: stage,
        logout_source: source,
        ...(diagnostic.destinationOrigin
          ? {destination_origin: diagnostic.destinationOrigin}
          : {}),
      })
    } catch {
      // Diagnostics must never interfere with logout.
    }
  }

  /**
   * Prevent the next login-page visit from immediately starting SSO. The
   * marker is one-shot and short-lived so a later, unrelated visit behaves
   * normally.
   */
  suppressNextAutomaticLogin(source: LogoutSource): void {
    const marker: LogoutMarker = {createdAt: Date.now(), source}
    this.safeSessionStorageSet(JUST_LOGGED_OUT_KEY, JSON.stringify(marker))
    this.record('login-suppression-armed', source)
  }

  consumeAutomaticLoginSuppression(): LogoutSource | null {
    const raw = this.safeSessionStorageGet(JUST_LOGGED_OUT_KEY)
    this.safeSessionStorageRemove(JUST_LOGGED_OUT_KEY)
    if (!raw) {
      return null
    }

    try {
      const marker = JSON.parse(raw) as Partial<LogoutMarker>
      if (
        typeof marker.createdAt !== 'number' ||
        !this.isLogoutSource(marker.source)
      ) {
        return null
      }
      if (Date.now() - marker.createdAt > MARKER_MAX_AGE_MS) {
        this.record('login-suppression-expired', marker.source)
        return null
      }
      this.record('login-suppression-consumed', marker.source)
      return marker.source
    } catch {
      return null
    }
  }

  resetForAuthenticatedSession(): void {
    this.inProgress = false
    this.safeSessionStorageRemove(JUST_LOGGED_OUT_KEY)
  }

  private isLogoutSource(value: unknown): value is LogoutSource {
    return (
      value === 'user' || value === 'http-401' || value === 'refresh-failure'
    )
  }

  private safeOrigin(url: string): string {
    try {
      return new URL(url, window.location.origin).origin
    } catch {
      return 'invalid'
    }
  }

  private readDiagnosticHistory(): LogoutDiagnostic[] {
    const raw = this.safeSessionStorageGet(LOGOUT_DIAGNOSTIC_KEY)
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw) as unknown
      return Array.isArray(parsed) ? (parsed as LogoutDiagnostic[]) : []
    } catch {
      return []
    }
  }

  private safeSessionStorageGet(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key)
    } catch {
      return null
    }
  }

  private safeSessionStorageSet(key: string, value: string): void {
    try {
      window.sessionStorage.setItem(key, value)
    } catch {
      // Storage can be unavailable in hardened/private browser contexts.
    }
  }

  private safeSessionStorageRemove(key: string): void {
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      // Storage can be unavailable in hardened/private browser contexts.
    }
  }
}
